import { graphql } from '@/gql/codegen/gql';
import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

const COUNT_STUDY_TIME_MUTATION = graphql(/* GraphQL */ `
  mutation EmbaCountStudyTime($input: CountStudyTimeMutationInput!) {
    embaCountStudyTime(input: $input) {
      success
    }
  }
`);

const COUNT_INTERVAL = 60000;

const useCountStudyTimeMutation = () => {
  const [countStudyTimeMutation, { error: countStudyTimeMutationError }] =
    useMutation(COUNT_STUDY_TIME_MUTATION, {});

  const countStudyTime = useCallback(
    async (
      embaUserAttendanceSlug: string,
      embaCourseLessonSlug: string | undefined,
    ): Promise<void> => {
      await countStudyTimeMutation({
        variables: {
          input: {
            embaUserAttendanceSlug,
            embaCourseLessonSlug,
          },
        },
      });
    },
    [countStudyTimeMutation],
  );

  return {
    countStudyTime,
    countStudyTimeMutationError,
  };
};

export const useCountStudyTime = ({
  userAttendanceSlug,
  lessonSlug,
}: {
  userAttendanceSlug: string;
  lessonSlug: string | undefined;
}) => {
  const { countStudyTime, countStudyTimeMutationError } =
    useCountStudyTimeMutation();

  useEffect(() => {
    let intervalId: number | undefined;

    const handleVisibilityChange = () => {
      // タブがアクティブな場合、タイマーを開始
      if (document.visibilityState === 'visible') {
        countStudyTime(userAttendanceSlug, lessonSlug);

        intervalId = window.setInterval(() => {
          countStudyTime(userAttendanceSlug, lessonSlug);
        }, COUNT_INTERVAL);
      } else {
        // タブが非アクティブな場合、タイマーをクリア
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
          intervalId = undefined;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    handleVisibilityChange();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [countStudyTime, userAttendanceSlug, lessonSlug]);

  if (countStudyTimeMutationError) {
    throw countStudyTimeMutationError;
  }
};
